import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import ServiceWrapper from "../components/ServiceWrapper";
import { api } from "../utils/api";
import { track } from "../utils/track";

/**
 * 문의 채팅 (새 디자인, 2026-10). 지금까지 보낸 문의와 관리자 답변을 시간순 말풍선으로 보여주고,
 * 아래 입력창으로 새 문의를 보낸다. (서버 구조는 그대로: 문의 1건 = 내 말풍선, 답변 = 코드스코어 말풍선)
 */
interface IInquiry {
  id: number;
  title: string;
  contents: string;
  answer: string | null;
  answer_date: string | null;
  status: number;
  created_at: string;
}

const fmt = (s?: string | null) => {
  if (!s) return "";
  const d = new Date(s.replace(" ", "T"));
  if (isNaN(d.getTime())) return s;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const SupportChat: React.FC = () => {
  const [items, setItems] = useState<IInquiry[] | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const { data } = await api.get("/inquiry");
      const list: IInquiry[] = (data?.data?.inquiries || []).slice().reverse(); // 오래된 것부터
      setItems(list);
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [items]);

  const send = async () => {
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    track("support:send");
    setErr("");
    try {
      const title = v.replace(/\s+/g, " ").slice(0, 30);
      await api.post("/inquiry/register", { title, contents: v });
      setText("");
      await load();
    } catch (e) {
      setErr("보내지 못했어요. 잠시 뒤 다시 시도해 주세요.");
    }
    setSending(false);
  };

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <Head>
          <h1>문의하기</h1>
          <span>답변이 오면 알림으로 알려드려요</span>
        </Head>

        <Thread>
          {items && items.length === 0 && (
            <Empty>
              <strong>무엇이든 물어보세요</strong>
              <span>악보 신청, 결제·구독, 앱 사용 중 불편한 점을 남겨 주시면 코드스코어가 답해 드려요.</span>
            </Empty>
          )}
          {items?.map((q) => (
            <React.Fragment key={q.id}>
              <Row mine>
                <Bubble mine>
                  {q.title && !q.contents.startsWith(q.title.slice(0, 10)) && <BubbleTitle>{q.title}</BubbleTitle>}
                  {q.contents}
                </Bubble>
                <Meta>{fmt(q.created_at)}</Meta>
              </Row>
              {q.answer ? (
                <Row>
                  <Avatar aria-hidden="true">d</Avatar>
                  <div>
                    <Name>코드스코어</Name>
                    <Bubble>{q.answer}</Bubble>
                    <Meta>{fmt(q.answer_date)}</Meta>
                  </div>
                </Row>
              ) : (
                <Waiting>답변을 준비하고 있어요</Waiting>
              )}
            </React.Fragment>
          ))}
          <div ref={endRef} />
        </Thread>

        <Composer
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="메시지를 입력하세요"
            rows={1}
            aria-label="문의 내용"
          />
          <button type="submit" disabled={!text.trim() || sending} aria-label="보내기">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12 20 4l-6 16-3-7z" /></svg>
          </button>
        </Composer>
        {err && <Err>{err}</Err>}
      </Page>
    </ServiceWrapper>
  );
};

export default SupportChat;

const Page = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 16px 16px 150px;
  color: #f3f4f6;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0 4px 12px;
  & > h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #f3f4f6;
  }
  & > span {
    font-size: 12px;
    color: #7c8392;
  }
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row = styled.div<{ mine?: boolean }>`
  display: flex;
  flex-direction: ${({ mine }) => (mine ? "column" : "row")};
  align-items: ${({ mine }) => (mine ? "flex-end" : "flex-start")};
  gap: 8px;
`;

const Bubble = styled.div<{ mine?: boolean }>`
  max-width: 80vw;
  padding: 10px 14px;
  border-radius: ${({ mine }) => (mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px")};
  background: ${({ mine }) => (mine ? "#5b8cff" : "#1e2129")};
  color: ${({ mine }) => (mine ? "#0d0e12" : "#f3f4f6")};
  font-size: 15px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

const BubbleTitle = styled.strong`
  display: block;
  margin-bottom: 4px;
`;

const Meta = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #7c8392;
`;

const Avatar = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: #2b3550;
  color: #8fb0ff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Name = styled.span`
  display: block;
  font-size: 12px;
  color: #a3a9b5;
  margin-bottom: 4px;
`;

const Waiting = styled.span`
  align-self: flex-start;
  font-size: 12px;
  color: #7c8392;
  padding-left: 4px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 48px 12px;
  text-align: center;
  word-break: keep-all;
  & > strong {
    font-size: 18px;
    color: #f3f4f6;
  }
  & > span {
    font-size: 14px;
    line-height: 1.6;
    color: #a3a9b5;
  }
`;

const Composer = styled.form`
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(62px + env(safe-area-inset-bottom));
  @media (min-width: 700px) {
    width: min(100%, 760px);
    margin: 0 auto;
    border-left: 1px solid #22252d;
    border-right: 1px solid #22252d;
    box-sizing: border-box;
  }
  @media (min-width: 1024px) {
    bottom: 0;
  }
  z-index: 800;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  background: #121318;
  border-top: 1px solid #22252d;

  & > textarea {
    flex: 1;
    min-height: 44px;
    max-height: 120px;
    padding: 11px 14px;
    border-radius: 22px;
    border: 1px solid #2a2e38;
    background: #16181e;
    color: #f3f4f6;
    font-size: 15px;
    line-height: 1.4;
    resize: none;
    outline: 0;
    box-sizing: border-box;
  }
  & > button {
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 22px;
    border: 0;
    background: #5b8cff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  & > button:disabled {
    background: #2a2e38;
  }
  & > button > svg {
    width: 20px;
    height: 20px;
    fill: #0d0e12;
  }

  @media (min-width: 769px) {
    bottom: 0;
  }
`;

const Err = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #ff9a9a;
  text-align: center;
`;
